/**
 * Meta progression: Shards and the unlock tree (TRD-08 Part B).
 *
 * Every function here is pure — it takes save state and returns new save state — because
 * this is the code that decides what a player permanently owns, and "permanently" is
 * exactly where an untested mutation becomes an unrecoverable support ticket. The React
 * layer applies the results; it never computes them.
 */
import { UNLOCK_TREE, type UnlockNode } from '@prism/content';
import type { SaveData } from '../persistence/save.js';

// ── Shards (R-08-16 / R-08-17) ────────────────────────────────────────────────────────

/** Waves whose clear is a boss kill. Matches the authored wave table's boss cadence. */
const BOSS_EVERY = 5;

/** First-time wave milestones, global across maps. */
const WAVE_MILESTONES = [10, 15, 20, 25] as const;

/**
 * R-08-16a — the award scale matches the ~1670-Shard tree (R-08-18b). When the tree
 * was 100 nodes a run paid 3-6; at ten times the tree the same trickle would strand
 * most of it. A decent run now pays 10-15 and a first full win ~40, keeping the full
 * circle a long project without making any single ladder feel unreachable.
 */
const SHARDS_PER_BOSS = 2;
const SHARDS_PER_MILESTONE = 2;
const SHARDS_FIRST_WIN = 8;

export interface ShardAward {
  total: number;
  /** One line per source, for the run summary (R-09-25). */
  breakdown: { reason: string; shards: number }[];
}

export interface RunOutcome {
  wavesCleared: number;
  victory: boolean;
  /** Stable key for this run's map — a map id, or the date string for a daily. */
  mapKey: string;
}

/** The best wave the player has EVER cleared, across every map they have played. */
export function bestWaveEver(save: SaveData): number {
  let best = 0;
  for (const v of Object.values(save.stats.bestWave)) if (v > best) best = v;
  return best;
}

/**
 * What a finished run earns (R-08-16). Pure: reads the save, returns the award.
 *
 * "Reaching" a milestone means CLEARING that wave — the same measure `bestWave` records,
 * so the first-time check and the stat it reads can never disagree.
 */
export function shardAward(outcome: RunOutcome, save: SaveData): ShardAward {
  const breakdown: ShardAward['breakdown'] = [];

  breakdown.push({ reason: 'Run completed', shards: 1 });

  // Every second wave pays one — the workhorse line that makes ANY run progress the
  // tree, not just record-setting ones (R-08-16a).
  const waveShards = Math.floor(outcome.wavesCleared / 2);
  if (waveShards > 0) {
    breakdown.push({ reason: `${outcome.wavesCleared} waves cleared`, shards: waveShards });
  }

  const bosses = Math.floor(outcome.wavesCleared / BOSS_EVERY);
  if (bosses > 0) {
    breakdown.push({
      reason: `${bosses} boss${bosses > 1 ? 'es' : ''} cleared`,
      shards: bosses * SHARDS_PER_BOSS,
    });
  }

  const previousBest = bestWaveEver(save);
  for (const milestone of WAVE_MILESTONES) {
    if (outcome.wavesCleared >= milestone && previousBest < milestone) {
      breakdown.push({
        reason: `First time past wave ${milestone}`,
        shards: SHARDS_PER_MILESTONE,
      });
    }
  }

  if (outcome.victory && (save.stats.winsPerMap[outcome.mapKey] ?? 0) === 0) {
    breakdown.push({ reason: 'First win on this map', shards: SHARDS_FIRST_WIN });
  }

  return { total: breakdown.reduce((sum, b) => sum + b.shards, 0), breakdown };
}

/**
 * Fold a finished run into the save: stats first, then the award computed against the
 * PRE-run stats — a first-time milestone must read the state before this run set it.
 *
 * `shardsEarned` only ever grows (R-08-17). Returns a new save; never mutates.
 */
export function applyRunOutcome(
  save: SaveData,
  outcome: RunOutcome,
): {
  save: SaveData;
  award: ShardAward;
} {
  const award = shardAward(outcome, save);

  const next: SaveData = {
    ...save,
    shardsEarned: save.shardsEarned + award.total,
    stats: {
      ...save.stats,
      runsPlayed: save.stats.runsPlayed + 1,
      bestWave: {
        ...save.stats.bestWave,
        [outcome.mapKey]: Math.max(save.stats.bestWave[outcome.mapKey] ?? 0, outcome.wavesCleared),
      },
      winsPerMap: {
        ...save.stats.winsPerMap,
        [outcome.mapKey]: (save.stats.winsPerMap[outcome.mapKey] ?? 0) + (outcome.victory ? 1 : 0),
      },
    },
  };

  return { save: next, award };
}

// ── The tree (R-08-18 / R-08-21) ──────────────────────────────────────────────────────

export function shardsAvailable(save: SaveData): number {
  return save.shardsEarned - save.shardsSpent;
}

export type NodeState = 'owned' | 'affordable' | 'locked' | 'unaffordable';

/** The tree is static data; index it once for the O(1) lookups a 1000-node UI needs. */
const NODE_BY_ID = new Map(UNLOCK_TREE.map((n) => [n.id, n]));

/** The four visual states of the tree screen (TRD-09 R-09-22). */
export function nodeState(node: UnlockNode, save: SaveData): NodeState {
  const owned = new Set(save.ownedNodes);
  if (owned.has(node.id)) return 'owned';
  if (!node.requires.every((r) => owned.has(r))) return 'locked';
  return node.cost <= shardsAvailable(save) ? 'affordable' : 'unaffordable';
}

/**
 * Every node's state in one pass — the per-node `nodeState` rebuilds the owned set
 * each call, which is fine for a click handler and quadratic for a full render.
 */
export function nodeStates(save: SaveData): Map<string, NodeState> {
  const owned = new Set(save.ownedNodes);
  const available = shardsAvailable(save);
  const states = new Map<string, NodeState>();
  for (const node of UNLOCK_TREE) {
    states.set(
      node.id,
      owned.has(node.id)
        ? 'owned'
        : !node.requires.every((r) => owned.has(r))
          ? 'locked'
          : node.cost <= available
            ? 'affordable'
            : 'unaffordable',
    );
  }
  return states;
}

/**
 * Buy one node. Returns the new save, or null if the purchase is illegal — already
 * owned, prerequisites missing, or unaffordable. Null rather than a throw because an
 * illegal click is an ordinary event on this screen, not an exception.
 */
export function buyNode(save: SaveData, nodeId: string): SaveData | null {
  const node = NODE_BY_ID.get(nodeId);
  if (!node) return null;
  if (nodeState(node, save) !== 'affordable') return null;

  return {
    ...save,
    shardsSpent: save.shardsSpent + node.cost,
    ownedNodes: [...save.ownedNodes, node.id],
  };
}

/**
 * Respec (R-08-21): every spent Shard back, all nodes released, free, no partial forms.
 * `shardsEarned` is untouched — the refund reappears as available, never as earned.
 */
export function respec(save: SaveData): SaveData {
  return { ...save, shardsSpent: 0, ownedNodes: [] };
}

/**
 * Every node id whose ownership eventually leads to `nodeId` — its prerequisite chain,
 * for the hover treatment that dims everything else (R-09-24).
 */
export function prerequisiteChain(nodeId: string): Set<string> {
  const chain = new Set<string>();
  const walk = (id: string): void => {
    if (chain.has(id)) return;
    chain.add(id);
    for (const req of NODE_BY_ID.get(id)?.requires ?? []) walk(req);
  };
  walk(nodeId);
  return chain;
}
